import deburr from 'lodash/deburr';
import React, { useState, useEffect } from 'react';
import AutoSuggest from 'react-autosuggest';
import { withLocalize } from 'react-localize-redux';
import ghLogoImage from './gh-logo.png';

/**
 * @typedef {Object} GolfCourse
 * @property {string} golfclub - The name of the golf club.
 * @property {string} country - The country of the golf club.
 * @property {number} [participating] - Indicates whether the golf club is participating.
 * @property {string} [date] - The date of the event.
 * @property {number | string} id - ID of the golf club.
 * @property {string} [path] - Path for navigation to the golf club.
 * @property {string} [abbr] - Country abbreviation for the golf club.
 * @property {number | string} [cId] - Country ID.
 * @property {number | string} [dId] - District ID.
 */

/**
 * @typedef {Object} Country
 * @property {number | string} id - Country ID (cId).
 * @property {string} name - Country name.
 * @property {string} abbr - Country code.
 */

/**
 * @typedef {Object} District
 * @property {number | string} id - District ID (dId).
 * @property {string} name - District name.
 * @property {number | string} cId - ID of the country to which the district belongs.
 * @property {string} countryAbbr - Country code of the country to which the district belongs.
 */

/**
 * @typedef {Object} SuggestionItem
 * @property {'course' | 'country' | 'district'} type - Type of the suggestion.
 * @property {string} name - Display name of the suggestion.
 * @property {string | number} id - Unique ID of the suggestion entity.
 * @property {string} [countryAbbr] - Country abbreviation (for countries and districts).
 * @property {number} [participating] - Indicates whether the golf club is participating (for courses).
 * @property {string} [golfclub] - The name of the golf club (for courses).
 * @property {string} [abbr] - Country abbreviation for the golf club (for courses).
 * @property {number | string} [dId] - District ID (for courses).
 */

/**
 * Searches for golf clubs, countries, and districts matching the query.
 *
 * @param {GolfCourse[]} golfCourses - List of golf courses.
 * @param {Country[]} countries - List of countries.
 * @param {District[]} districts - List of districts.
 * @param {string} query - Search query.
 * @returns {SuggestionItem[]}
 */
const searchItems = (golfCourses, countries, districts, query) => {
  if (!query) {
    return [];
  }
  const normalizedQuery = deburr(query.toLowerCase().trim());
  if (normalizedQuery.length < 2) {
    return [];
  }

  const suggestions = [];

  // Search courses
  if (golfCourses) {
    golfCourses.forEach((course) => {
      if (course && course.golfclub && deburr(course.golfclub.toLowerCase()).includes(normalizedQuery)) {
        suggestions.push({
          type: 'course',
          name: course.golfclub,
          id: course.id,
          participating: course.participating,
          golfclub: course.golfclub,
          abbr: course.abbr,
          dId: course.dId
        });
      }
    });
  }

  // Search Countries
  if (countries) {
    countries.forEach((country) => {
      if (country && country.name && deburr(country.name.toLowerCase()).includes(normalizedQuery)) {
        suggestions.push({
          type: 'country',
          name: country.name,
          id: country.id,
          countryAbbr: country.abbr
        });
      }
    });
  }

  // Search Districts
  if (districts) {
    districts.forEach((district) => {
      if (district && district.name && deburr(district.name.toLowerCase()).includes(normalizedQuery)) {
        suggestions.push({
          type: 'district',
          name: `${district.name}, ${district.countryAbbr}`,
          id: district.id,
          countryAbbr: district.countryAbbr
        });
      }
    });
  }
  return suggestions;
};

/**
 * Renders a suggestion item.
 *
 * @param {SuggestionItem} suggestion - The suggestion to render.
 * @returns {JSX.Element}
 */
const renderSuggestion = (suggestion) => {
  let showGhLogo = false;

  if (suggestion.type === 'course') {
    showGhLogo = suggestion.participating === 1;
  }

  return (
    <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
      <span className="d-flex align-items-center" style={{ gap: 8 }}>
        {showGhLogo && <img src={ghLogoImage} alt="Golfamore logo" width={24} />}
        {suggestion.name}
      </span>
      <small className="text-muted flex-shrink-0" style={{ fontWeight: 'normal' }}>
        {suggestion.type}
      </small>
    </div>
  );
};

/**
 * Renders the suggestions container.
 *
 * @param {{ containerProps: React.HTMLAttributes<HTMLDivElement>, children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
const renderSuggestionsContainer = ({ containerProps, children }) => {
  return (
    <div {...containerProps} style={{ maxHeight: 'calc(35px * 7.6)', overflowY: 'auto', overscrollBehavior: 'none' }}>
      {children}
    </div>
  );
};

/**
 * @typedef {Object} SearchAutoSuggestInputProps
 * @property {GolfCourse[]} [golfCourses] - List of golf courses to suggest.
 * @property {Country[]} [countries] - List of countries to suggest.
 * @property {District[]} [districts] - List of districts to suggest.
 * @property {function(SuggestionItem): void} onChange - Callback function invoked when a suggestion is selected.
 * It receives the selected SuggestionItem.
 * @property {Object} [inputProps] - props for input field.
 * @property {string} [value] - default input value.
 */

/**
 * SearchAutoSuggestInput component.
 *
 * @param {SearchAutoSuggestInputProps} props - Component properties.
 * @returns {JSX.Element}
 */
const SearchAutoSuggestInput = (props) => {
  const { translate, golfCourses, countries, districts, onChange, inputProps, value: defaultValue = '' } = props;

  const [value, setValue] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (defaultValue) {
      setSuggestions(searchItems(golfCourses, countries, districts, defaultValue));
    }
  }, []);

  return (
    <AutoSuggest
      suggestions={suggestions}
      onSuggestionsFetchRequested={({ value }) => {
        setSuggestions(searchItems(golfCourses, countries, districts, value));
      }}
      onSuggestionsClearRequested={() => {
        setSuggestions([]);
      }}
      getSuggestionValue={(suggestion) => suggestion.name}
      renderSuggestion={renderSuggestion}
      renderSuggestionsContainer={renderSuggestionsContainer}
      onSuggestionSelected={(event, { suggestion }) => {
        onChange(suggestion);
      }}
      shouldRenderSuggestions={(value) => value.trim().length > 1}
      inputProps={{
        placeholder: translate('default.free-search'),
        value,
        type: 'search',
        onChange: (event, { newValue }) => {
          setValue(newValue);
        },
        ...inputProps
      }}
    />
  );
};

export default withLocalize(SearchAutoSuggestInput);
